/**
 * Owner Dashboard - Private Collection Management Interface
 * Elvis Aloha Collection
 * 
 * A protected admin workspace providing:
 * - Collection overview with statistics
 * - Ticket records management (CRUD)
 * - Verification queue
 * - Media review
 * - Graceland 50 reconciliation workspace
 * - Audit history
 * - Settings
 * 
 * Security: All actions validated server-side. JWT auth required.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route, NavLink, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import MapEditorStudio from '../../components/MapEditorStudioV3';
import { ArtifactsListSection, ArtifactEditorSection, InventoryDashboardSection, InquiriesSection } from './ArtifactManagement';
import CollectionGroupManagement from './CollectionGroupManagement';
import CollectionGroupEditor from './CollectionGroupEditor';
import '../../styles/map-editor.css';
import { takePendingOwnerUser } from '../../utils/ownerAuthTransfer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// AUTH HOOKS & HELPERS
// ============================================================================

const useOwnerAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    // If we just navigated here straight from a successful login, the
    // owner's identity is already known (the login response returned it) -
    // use it directly instead of immediately re-fetching /auth/me, which
    // used to race the just-issued session cookie and log a spurious 401
    // during the login->dashboard transition (React StrictMode's dev-only
    // double-effect-invocation made this worse by firing the race twice).
    // takePendingOwnerUser() is in-memory only and single-use, so a hard
    // refresh, direct URL load, or expired session always falls through to
    // the real /auth/me check below.
    const handoffUser = takePendingOwnerUser();
    if (handoffUser?.role === 'owner_admin') {
      setUser(handoffUser);
      setLoading(false);
      return;
    }
    
    const controller = new AbortController();
    
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/auth/me`, {
          credentials: 'include',
          signal: controller.signal
        });
        
        if (res.ok) {
          const userData = await res.json();
          if (userData.role === 'owner_admin') {
            setUser(userData);
          } else {
            setError('Owner access required');
            navigate('/');
          }
        } else {
          setError('Not authenticated');
          navigate('/manage');
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError('Authentication failed');
        navigate('/manage');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    
    checkAuth();
    return () => controller.abort();
  }, [navigate]);
  
  const logout = useCallback(async () => {
    await fetch(`${API_BASE}/api/seat-map/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    setUser(null);
    navigate('/');
  }, [navigate]);
  
  return { user, loading, error, logout };
};

// ============================================================================
// SIDEBAR NAVIGATION
// ============================================================================

const DASHBOARD_SECTIONS = [
  { id: 'overview', path: '', label: 'Collection Overview', icon: '📊' },
  { id: 'artifacts', path: 'artifacts', label: 'Artifacts & Inventory', icon: '🏺' },
  { id: 'artifact-create', path: 'artifacts/new', label: 'Add Artifact', icon: '➕', hidden: true },
  { id: 'artifact-edit', path: 'artifacts/:id', label: 'Edit Artifact', icon: '✏️', hidden: true },
  { id: 'collections', path: 'collections', label: 'Collection Groups', icon: '📁' },
  { id: 'collection-new', path: 'collections/new', label: 'New Collection', icon: '➕', hidden: true },
  { id: 'collection-edit', path: 'collections/:groupId', label: 'Edit Collection', icon: '✏️', hidden: true },
  { id: 'inventory', path: 'inventory', label: 'Financial Dashboard', icon: '💰' },
  { id: 'inquiries', path: 'inquiries', label: 'Inquiries', icon: '📬' },
  { id: 'map-editor', path: 'map-editor', label: 'Map Editor Studio', icon: '🗺️' },
  { id: 'tickets', path: 'tickets', label: 'Ticket Records', icon: '🎫' },
  { id: 'blanks', path: 'blanks', label: 'Blank Tickets', icon: '📄' },
  { id: 'verification', path: 'verification', label: 'Verification Queue', icon: '✓' },
  { id: 'media', path: 'media', label: 'Media Review', icon: '🖼️' },
  { id: 'graceland', path: 'graceland-display', label: 'Graceland Display', icon: '🏛️' },
  { id: 'duplicates', path: 'duplicates', label: 'Duplicate Candidates', icon: '🔀' },
  { id: 'sales', path: 'sales', label: 'Sales & Appearances', icon: '📋' },
  { id: 'audit', path: 'audit', label: 'Audit History', icon: '📜' },
  { id: 'archived', path: 'archived', label: 'Archived Records', icon: '🗃️' },
  { id: 'settings', path: 'settings', label: 'Settings', icon: '⚙️' },
];

const DashboardSidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const currentPath = location.pathname.replace('/owner-dashboard', '').replace(/^\//, '') || '';
  
  return (
    <aside 
      className="owner-sidebar"
      style={{
        width: '260px',
        background: '#171717',
        minHeight: '100vh',
        padding: '0',
        flexShrink: 0
      }}
    >
      {/* Brand Header - Compact and Centered */}
      <div className="owner-brand" style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '14px 16px 10px',
        background: '#171717'
      }}>
        {/* Logo - Clean Elvis artwork on white background */}
        <a href="/" style={{ display: 'block', marginBottom: '10px' }}>
          <img 
            src="/assets/branding/logo-sidebar-white.png" 
            alt="Elvis Aloha Collection"
            className="owner-brand-logo"
            style={{ 
              width: '140px',
              height: '140px',
              objectFit: 'cover',
              objectPosition: 'center 42%',
              display: 'block',
              margin: '0 auto',
              borderRadius: '50%',
              border: '2px solid #C9A962',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}
          />
        </a>
        
        <div className="owner-brand-label" style={{
          fontSize: '9px',
          color: '#C9A962',
          fontWeight: 700,
          letterSpacing: '0.08em',
          marginBottom: '4px',
          textTransform: 'uppercase'
        }}>
          OWNER DASHBOARD
        </div>
        
        {user && (
          <div className="owner-user-card" style={{
            padding: '5px 8px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '4px',
            marginTop: '4px',
            width: '100%'
          }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: '10px' }}>
              {user.name}
            </div>
            <div style={{ color: '#999', fontSize: '9px' }}>
              {user.email}
            </div>
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <nav>
        {DASHBOARD_SECTIONS.filter(s => !s.hidden).map(section => {
          const isActive = currentPath === section.path || 
            (section.path === '' && currentPath === '') ||
            (section.path && currentPath.startsWith(section.path));
          
          return (
            <NavLink
              key={section.id}
              to={`/owner-dashboard/${section.path}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 20px',
                color: isActive ? '#fff' : '#888',
                background: isActive ? 'rgba(201, 169, 98, 0.15)' : 'transparent',
                borderLeft: isActive ? '3px solid #C9A962' : '3px solid transparent',
                textDecoration: 'none',
                fontSize: '14px',
                fontFamily: "'Source Sans 3', sans-serif",
                transition: 'all 0.15s'
              }}
            >
              <span style={{ fontSize: '16px' }}>{section.icon}</span>
              {section.label}
            </NavLink>
          );
        })}
      </nav>
      
      {/* Footer Actions */}
      <div style={{ 
        padding: '20px', 
        marginTop: 'auto',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <NavLink
          to="/seat-registry"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '6px',
            color: '#888',
            textDecoration: 'none',
            fontSize: '13px',
            marginBottom: '8px'
          }}
        >
          ← Public Registry
        </NavLink>
        
        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '6px',
            color: '#888',
            cursor: 'pointer',
            fontSize: '13px',
            fontFamily: "'Source Sans 3', sans-serif"
          }}
        >
          Sign Out
        </button>
      </div>
    </aside>
  );
};

// ============================================================================
// DASHBOARD SECTIONS
// ============================================================================

// Placeholder for ticket editor modal (not yet implemented)
const TicketEditorModal = ({ ticket, onClose, isNew = false }) => {
  const [formData, setFormData] = useState({
    registry_reference: ticket?.registry_reference || '',
    // Section/row/seat are at TOP level in the ticket object, not nested
    seat_section: ticket?.section || ticket?.seat_info?.section || '',
    seat_row: ticket?.row || ticket?.seat_info?.row || '',
    seat_number: ticket?.seat || ticket?.seat_info?.seat || '',
    ticket_type: ticket?.ticket_type || 'full_ticket',
    ticket_form: ticket?.ticket_form || 'seat_numbered_full',
    verification_status: ticket?.verification_status || 'documented',
    ownership_status: ticket?.ownership_status || 'private_collection',
    psa_cert_number: ticket?.psa_cert_number || ticket?.certification_number || '',
    psa_grade: ticket?.psa_grade || ticket?.grade || '',
    notes: ticket?.notes || '',
    private_notes: ticket?.private_notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Match backend TicketRecordCreate model - section/row/seat at top level
      const payload = {
        registry_reference: formData.registry_reference,
        section: formData.seat_section || null,
        row: formData.seat_row || null,
        seat: formData.seat_number || null,
        ticket_form: formData.ticket_form,
        ownership_status: formData.ownership_status,
        certification_number: formData.psa_cert_number || null,
        grade: formData.psa_grade || null
      };

      const url = isNew 
        ? `${API_BASE}/api/seat-map/tickets`
        : `${API_BASE}/api/seat-map/tickets/${ticket.id}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json();
        // Handle Pydantic validation errors which return an array
        let errorMessage = 'Failed to save ticket';
        if (errData.detail) {
          if (Array.isArray(errData.detail)) {
            errorMessage = errData.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
          } else if (typeof errData.detail === 'string') {
            errorMessage = errData.detail;
          } else {
            errorMessage = JSON.stringify(errData.detail);
          }
        }
        throw new Error(errorMessage);
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.6)', 
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            {isNew ? 'Add New Ticket' : `Edit Ticket: ${ticket?.registry_reference || 'Unknown'}`}
          </h2>
          <button 
            onClick={onClose}
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: '24px', 
              cursor: 'pointer',
              color: '#888'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {error && (
            <div style={{ 
              padding: '12px', 
              background: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '6px',
              marginBottom: '20px',
              color: '#c00'
            }}>
              {error}
            </div>
          )}

          {/* Seat Location */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#333' }}>
              Seat Location
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Section</label>
                <input 
                  type="text"
                  value={formData.seat_section}
                  onChange={(e) => handleChange('seat_section', e.target.value)}
                  placeholder="e.g. AA, H, L"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Row</label>
                <input 
                  type="text"
                  value={formData.seat_row}
                  onChange={(e) => handleChange('seat_row', e.target.value)}
                  placeholder="e.g. 1, 2, 3"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Seat</label>
                <input 
                  type="text"
                  value={formData.seat_number}
                  onChange={(e) => handleChange('seat_number', e.target.value)}
                  placeholder="e.g. 1, 2, 3"
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Registry Reference */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Registry Reference</label>
            <input 
              type="text"
              value={formData.registry_reference}
              onChange={(e) => handleChange('registry_reference', e.target.value)}
              placeholder="e.g. EAC-AA-001"
              style={inputStyle}
            />
          </div>

          {/* Ticket Type & Form */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Ticket Type</label>
              <select 
                value={formData.ticket_type}
                onChange={(e) => handleChange('ticket_type', e.target.value)}
                style={inputStyle}
              >
                <option value="full_ticket">Full Ticket</option>
                <option value="stub">Stub</option>
                <option value="blank">Blank</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Ticket Form</label>
              <select 
                value={formData.ticket_form}
                onChange={(e) => handleChange('ticket_form', e.target.value)}
                style={inputStyle}
              >
                <option value="seat_numbered_full">Seat Numbered Full</option>
                <option value="blank_full">Blank Full</option>
                <option value="used_stub">Used Stub</option>
                <option value="other_material">Other Material</option>
              </select>
            </div>
          </div>

          {/* Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Verification Status</label>
              <select 
                value={formData.verification_status}
                onChange={(e) => handleChange('verification_status', e.target.value)}
                style={inputStyle}
              >
                <option value="documented">Documented</option>
                <option value="verified">Verified</option>
                <option value="psa_graded">PSA Graded</option>
                <option value="disputed">Disputed</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Ownership Status</label>
              <select 
                value={formData.ownership_status}
                onChange={(e) => handleChange('ownership_status', e.target.value)}
                style={inputStyle}
              >
                <option value="principal_archive">Principal Archive</option>
                <option value="private_collection">Private Collection</option>
                <option value="graceland_custody">Graceland Custody</option>
                <option value="current_owner_not_established">Owner Not Established</option>
              </select>
            </div>
          </div>

          {/* PSA Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>PSA Cert Number</label>
              <input 
                type="text"
                value={formData.psa_cert_number}
                onChange={(e) => handleChange('psa_cert_number', e.target.value)}
                placeholder="Optional"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>PSA Grade</label>
              <input 
                type="text"
                value={formData.psa_grade}
                onChange={(e) => handleChange('psa_grade', e.target.value)}
                placeholder="e.g. 8, 9, 10"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Public Notes</label>
            <textarea 
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Notes visible to public..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Private Notes (Owner Only)</label>
            <textarea 
              value={formData.private_notes}
              onChange={(e) => handleChange('private_notes', e.target.value)}
              placeholder="Private notes, not visible to public..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 24px',
                background: saving ? '#ccc' : '#C9A962',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : (isNew ? 'Create Ticket' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CollectionOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/inventory/stats`, {
          credentials: 'include'
        });
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);
  
  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading...</div>;
  }
  
  return (
    <div style={{ padding: '32px' }}>
      <h1 style={{
        margin: '0 0 8px',
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: '32px',
        fontWeight: 500
      }}>
        Collection Overview
      </h1>
      <p style={{ margin: '0 0 32px', color: '#666', fontSize: '14px' }}>
        Summary of the Elvis Aloha from Hawaii 1973 ticket collection
      </p>
      
      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '40px'
      }}>
        <StatCard 
          label="Total Inventory" 
          value={stats?.total_tickets || 0} 
          subtitle="June 21, 2021 baseline" 
        />
        <StatCard 
          label="Seat-Numbered" 
          value={stats?.seat_numbered || 0} 
          color="#C9A962" 
        />
        <StatCard 
          label="Blank Tickets" 
          value={stats?.blank || 0} 
          color="#888" 
        />
        <StatCard 
          label="Current Total" 
          value={stats?.collection_current_total || 0} 
          subtitle="Including acquisitions" 
        />
        <StatCard 
          label="Graceland Custody" 
          value={stats?.graceland_custody_count || 0} 
          color="#5B7BA3" 
        />
        <StatCard 
          label="Display Selection" 
          value={`${stats?.graceland_display_count || 0}/50`} 
          color="#FFD700" 
          subtitle="Identification in progress"
        />
      </div>
      
      {/* Section Breakdown */}
      <div style={{
        background: '#fafafa',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #e0e0e0'
      }}>
        <h3 style={{
          margin: '0 0 20px',
          fontSize: '14px',
          fontWeight: 700,
          color: '#888',
          letterSpacing: '0.05em'
        }}>
          SECTION DISTRIBUTION
        </h3>
        
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {stats?.sections && Object.entries(stats.sections).map(([section, count]) => (
            <div key={section} style={{
              padding: '16px 24px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{
                fontSize: '28px',
                fontWeight: 600,
                color: '#C9A962',
                fontFamily: "'Cormorant Garamond', serif"
              }}>
                {count}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                Section {section}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Quick Actions */}
      <div style={{ marginTop: '40px' }}>
        <h3 style={{
          margin: '0 0 16px',
          fontSize: '14px',
          fontWeight: 700,
          color: '#888',
          letterSpacing: '0.05em'
        }}>
          QUICK ACTIONS
        </h3>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <ActionButton label="Add New Ticket" to="/owner-dashboard/tickets?action=new" />
          <ActionButton label="Review Verifications" to="/owner-dashboard/verification" />
          <ActionButton label="Graceland 50 Workspace" to="/owner-dashboard/graceland-display" />
          <ActionButton label="Export Data" to="/owner-dashboard/settings?tab=export" />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, subtitle, color = '#1a1a1a' }) => (
  <div style={{
    padding: '20px 24px',
    background: 'white',
    borderRadius: '12px',
    border: '1px solid #e0e0e0'
  }}>
    <div style={{
      fontSize: '36px',
      fontWeight: 500,
      color,
      fontFamily: "'Cormorant Garamond', serif",
      lineHeight: 1
    }}>
      {value}
    </div>
    <div style={{
      fontSize: '14px',
      color: '#666',
      marginTop: '8px',
      fontFamily: "'Source Sans 3', sans-serif"
    }}>
      {label}
    </div>
    {subtitle && (
      <div style={{
        fontSize: '12px',
        color: '#aaa',
        marginTop: '4px'
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

const ActionButton = ({ label, to }) => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate(to)}
      style={{
        padding: '12px 20px',
        background: '#1a1a1a',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: "'Source Sans 3', sans-serif"
      }}
    >
      {label}
    </button>
  );
};

// Ticket Records Section (placeholder - will be expanded)
const TicketRecordsSection = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingTicket, setEditingTicket] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/tickets?page=${page}&per_page=25`, {
        credentials: 'include'
      });
      const data = await res.json();
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTickets();
  }, [page]);
  
  const filteredTickets = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return tickets;
    return tickets.filter(t =>
      (t.registry_reference || '').toLowerCase().includes(query) ||
      (t.certification_number || '').toLowerCase().includes(query)
    );
  }, [tickets, searchQuery]);
  
  const handleEdit = (ticket) => {
    setEditingTicket(ticket);
    setShowEditor(true);
  };
  
  const handleAddNew = () => {
    setEditingTicket(null); // null means new ticket
    setShowEditor(true);
  };
  
  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingTicket(null);
    fetchTickets(); // Refresh after editing
    // Scroll to top so user can see the updated ticket list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{
            margin: '0 0 4px',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '28px',
            fontWeight: 500
          }}>
            Ticket Records
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {total} total records
          </p>
        </div>
        
        <button 
          onClick={handleAddNew}
          style={{
            padding: '12px 20px',
            background: '#C9A962',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          + Add Ticket
        </button>
      </div>
      
      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <select style={{
          padding: '10px 16px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          fontSize: '14px',
          background: 'white'
        }}>
          <option value="">All Sections</option>
          <option value="AA">Section AA</option>
          <option value="BB">Section BB</option>
          <option value="H">Section H</option>
          <option value="L">Section L</option>
        </select>
        
        <select style={{
          padding: '10px 16px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          fontSize: '14px',
          background: 'white'
        }}>
          <option value="">All Statuses</option>
          <option value="principal_archive">Principal Archive</option>
          <option value="private_collection">Private Collection</option>
          <option value="current_owner_not_established">Owner Not Established</option>
        </select>
        
        <input
          type="text"
          placeholder="Search by registry ref, cert #..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="ticket-search-input"
          style={{
            padding: '10px 16px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            minWidth: '250px'
          }}
        />
      </div>
      
      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={thStyle}>Registry Ref</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}>Form</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Graceland</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#999', padding: '32px' }}>
                    No tickets match "{searchQuery}" on this page.
                  </td>
                </tr>
              ) : filteredTickets.map(ticket => (
                <tr key={ticket.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600, color: '#C9A962' }}>
                      {ticket.registry_reference}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {ticket.section ? `${ticket.section}-${ticket.row}-${ticket.seat}` : '—'}
                  </td>
                  <td style={tdStyle}>
                    {ticket.ticket_form?.replace(/_/g, ' ')}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 10px',
                      background: ticket.ownership_status === 'principal_archive' 
                        ? 'rgba(201, 169, 98, 0.15)' 
                        : '#f5f5f5',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {ticket.ownership_status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '4px 10px',
                      background: ticket.graceland_exhibition_status === 'selected_for_display'
                        ? 'rgba(255, 215, 0, 0.15)'
                        : '#f5f5f5',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: ticket.graceland_exhibition_status === 'selected_for_display'
                        ? '#b8860b'
                        : '#888'
                    }}>
                      {ticket.graceland_exhibition_status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button 
                      onClick={() => handleEdit(ticket)}
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div style={{
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #eee'
          }}>
            <div style={{ fontSize: '13px', color: '#888' }}>
              {searchQuery
                ? `Showing ${filteredTickets.length} matching record(s) on this page`
                : `Showing ${(page - 1) * 25 + 1}\u2013${Math.min(page * 25, total)} of ${total}`}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  opacity: page === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * 25 >= total}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: page * 25 >= total ? 'not-allowed' : 'pointer',
                  opacity: page * 25 >= total ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Ticket Editor Modal */}
      {showEditor && (
        <TicketEditorModal 
          ticket={editingTicket} 
          onClose={handleCloseEditor}
          isNew={!editingTicket}
        />
      )}
    </div>
  );
};

const thStyle = {
  padding: '14px 16px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 700,
  color: '#888',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #e0e0e0'
};

const tdStyle = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333'
};

// Placeholder sections
const BlankTicketsSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Blank & Unassigned Tickets
    </h1>
    <p style={{ color: '#666' }}>116 blank tickets from the original inventory.</p>
  </div>
);

const VerificationSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Verification Queue
    </h1>
    <p style={{ color: '#666' }}>Review and approve verification records.</p>
  </div>
);

const MediaReviewSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Media Review
    </h1>
    <p style={{ color: '#666' }}>Review uploaded images and documents.</p>
  </div>
);

const DuplicatesSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Duplicate Candidates
    </h1>
    <p style={{ color: '#666' }}>Review potential duplicate records.</p>
  </div>
);

const SalesSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Sales & Appearances
    </h1>
    <p style={{ color: '#666' }}>Documented sale and listing history.</p>
  </div>
);

const AuditSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Audit History
    </h1>
    <p style={{ color: '#666' }}>Complete audit trail of all changes.</p>
  </div>
);

const ArchivedSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Archived Records
    </h1>
    <p style={{ color: '#666' }}>View and restore archived ticket records.</p>
  </div>
);

const SettingsSection = () => (
  <div style={{ padding: '32px' }}>
    <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', margin: '0 0 16px' }}>
      Settings & Invitations
    </h1>
    <p style={{ color: '#666' }}>Manage account settings and user invitations.</p>
  </div>
);

// ============================================================================
// MAIN DASHBOARD LAYOUT
// ============================================================================

const OwnerDashboard = () => {
  const { user, loading, error, logout } = useOwnerAuth();
  
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#888' }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }
  
  if (error || !user) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#d44' }}>{error || 'Access denied'}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      data-testid="owner-dashboard"
      style={{ 
        display: 'flex', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}
    >
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <DashboardSidebar user={user} onLogout={logout} />
      
      <main style={{ 
        flex: 1, 
        overflow: 'auto',
        background: '#f5f5f5'
      }}>
        <Routes>
          <Route index element={<CollectionOverview />} />
          <Route path="artifacts" element={<ArtifactsListSection />} />
          <Route path="artifacts/new" element={<ArtifactEditorSection />} />
          <Route path="artifacts/:id" element={<ArtifactEditorSection />} />
          <Route path="collections" element={<CollectionGroupManagement />} />
          <Route path="collections/new" element={<CollectionGroupEditor />} />
          <Route path="collections/:groupId" element={<CollectionGroupEditor />} />
          <Route path="inventory" element={<InventoryDashboardSection />} />
          <Route path="inquiries" element={<InquiriesSection />} />
          <Route path="map-editor" element={<MapEditorStudio />} />
          <Route path="tickets" element={<TicketRecordsSection />} />
          <Route path="ticket-records" element={<TicketRecordsSection />} />
          <Route path="blanks" element={<BlankTicketsSection />} />
          <Route path="verification" element={<VerificationSection />} />
          <Route path="media" element={<MediaReviewSection />} />
          <Route path="graceland-display/*" element={<Graceland50Workspace />} />
          <Route path="duplicates" element={<DuplicatesSection />} />
          <Route path="sales" element={<SalesSection />} />
          <Route path="audit" element={<AuditSection />} />
          <Route path="archived" element={<ArchivedSection />} />
          <Route path="settings" element={<SettingsSection />} />
        </Routes>
      </main>
    </div>
  );
};

// ============================================================================
// GRACELAND 50 WORKSPACE
// ============================================================================

const Graceland50Workspace = () => {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ confirmed: 0, pending: 0 });
  
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/graceland-50/candidates`, {
          credentials: 'include'
        });
        const data = await res.json();
        setCandidates(data.candidates || []);
        setStats({
          confirmed: data.confirmed_count || 0,
          pending: data.pending_count || 0
        });
      } catch (err) {
        console.error('Failed to fetch candidates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCandidates();
  }, []);
  
  const handleConfirm = async (ticketId, data) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      const res = await fetch(`${API_BASE}/api/seat-map/graceland-50/confirm/${ticketId}`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (res.ok) {
        // Refresh candidates
        const refreshRes = await fetch(`${API_BASE}/api/seat-map/graceland-50/candidates`, {
          credentials: 'include'
        });
        const refreshData = await refreshRes.json();
        setCandidates(refreshData.candidates || []);
        setStats({
          confirmed: refreshData.confirmed_count || 0,
          pending: refreshData.pending_count || 0
        });
      }
    } catch (err) {
      console.error('Failed to confirm:', err);
    }
  };
  
  if (loading) {
    return (
      <div style={{ padding: '32px' }}>
        <div style={{ textAlign: 'center', color: '#888', padding: '60px' }}>
          Loading Graceland 50 workspace...
        </div>
      </div>
    );
  }
  
  return (
    <div 
      data-testid="graceland-50-workspace"
      style={{ padding: '32px', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          margin: '0 0 8px',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '28px',
          fontWeight: 500
        }}>
          Graceland Display Reconciliation
        </h1>
        <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px', maxWidth: '800px' }}>
          Fifty tickets were selected from the larger collection for the public Aloha display at Graceland.
          This workspace supports identification of the specific displayed tickets through photographic comparison.
        </p>
        
        {/* Progress */}
        <div style={{ display: 'flex', gap: '24px' }}>
          <div style={{
            padding: '16px 24px',
            background: 'rgba(255, 215, 0, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 215, 0, 0.3)'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#b8860b' }}>
              {stats.confirmed}/50
            </div>
            <div style={{ fontSize: '13px', color: '#8a7842' }}>
              Confirmed
            </div>
          </div>
          <div style={{
            padding: '16px 24px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#888' }}>
              {stats.pending}
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              Pending Review
            </div>
          </div>
        </div>
      </div>
      
      {/* Three-column layout */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '320px 1fr 400px',
        gap: '24px',
        flex: 1,
        minHeight: 0
      }}>
        {/* Column 1: Candidate List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #eee',
            background: '#fafafa'
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#666' }}>
              CANDIDATES ({candidates.length})
            </h3>
          </div>
          
          <div style={{ flex: 1, overflow: 'auto' }}>
            {candidates.map((c, i) => {
              const ticket = c.ticket;
              const isSelected = selectedCandidate?.ticket?.id === ticket.id;
              
              return (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedCandidate(c)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    background: isSelected ? 'rgba(201, 169, 98, 0.1)' : 'white',
                    borderLeft: isSelected ? '3px solid #C9A962' : '3px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>
                        {ticket.section}-{ticket.row}-{ticket.seat}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {ticket.registry_reference}
                      </div>
                    </div>
                    <div>
                      {c.is_confirmed ? (
                        <span style={{
                          padding: '4px 8px',
                          background: 'rgba(255, 215, 0, 0.15)',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#b8860b',
                          fontWeight: 600
                        }}>
                          ✓ Confirmed
                        </span>
                      ) : (
                        <span style={{
                          padding: '4px 8px',
                          background: '#f5f5f5',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#888'
                        }}>
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Column 2: Ticket Record & Images */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          overflow: 'auto'
        }}>
          {selectedCandidate ? (
            <div style={{ padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '20px', fontFamily: "'Cormorant Garamond', serif" }}>
                Section {selectedCandidate.ticket.section}, Row {selectedCandidate.ticket.row}, Seat {selectedCandidate.ticket.seat}
              </h3>
              
              {/* Ticket details grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <InfoField label="Registry Reference" value={selectedCandidate.ticket.registry_reference} />
                <InfoField label="Ticket Form" value={selectedCandidate.ticket.ticket_form?.replace(/_/g, ' ')} />
                <InfoField label="Certification" value={selectedCandidate.ticket.certification_number ? `#${selectedCandidate.ticket.certification_number}` : null} />
                <InfoField label="Grade" value={selectedCandidate.ticket.grade} />
                <InfoField label="Custody Status" value={selectedCandidate.ticket.graceland_custody_status?.replace(/_/g, ' ')} />
                <InfoField label="Exhibition Status" value={selectedCandidate.ticket.graceland_exhibition_status?.replace(/_/g, ' ')} />
              </div>
              
              {/* Ticket images placeholder */}
              <div style={{
                background: '#fafafa',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                color: '#888',
                marginBottom: '24px'
              }}>
                Ticket front/reverse images will appear here
              </div>
              
              {/* Identification confidence */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                  Identification Confidence
                </label>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  {selectedCandidate.identification_confidence?.replace(/_/g, ' ') || 'Pending review'}
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#888'
            }}>
              Select a candidate to view details
            </div>
          )}
        </div>
        
        {/* Column 3: Graceland Photo Comparison */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          overflow: 'auto'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #eee',
            background: '#fafafa'
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#666' }}>
              GRACELAND COMPARISON
            </h3>
          </div>
          
          <div style={{ padding: '24px' }}>
            {selectedCandidate ? (
              <>
                {/* Display photo placeholder */}
                <div style={{
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  aspectRatio: '4/3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#888',
                  marginBottom: '20px'
                }}>
                  Graceland display photograph
                </div>
                
                {/* Review Actions */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Review Action
                  </label>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <ReviewActionButton 
                      label="Confirmed as Displayed" 
                      active={selectedCandidate.is_confirmed}
                      color="#b8860b"
                    />
                    <ReviewActionButton label="Strong Photographic Match" />
                    <ReviewActionButton label="Probable Match" />
                    <ReviewActionButton label="More Evidence Required" />
                    <ReviewActionButton label="Not Part of Display" color="#d44" />
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                    Private Notes
                  </label>
                  <textarea
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      minHeight: '100px',
                      resize: 'vertical'
                    }}
                    placeholder="Add identification notes..."
                  />
                </div>
                
                <button
                  style={{
                    width: '100%',
                    marginTop: '16px',
                    padding: '14px',
                    background: '#C9A962',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Save Review
                </button>
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#888',
                padding: '40px'
              }}>
                Select a candidate to compare
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, marginBottom: '4px' }}>
      {label}
    </div>
    <div style={{ fontSize: '14px', color: value ? '#1a1a1a' : '#ccc' }}>
      {value || '—'}
    </div>
  </div>
);

const ReviewActionButton = ({ label, active, color = '#1a1a1a' }) => (
  <button
    style={{
      padding: '10px 14px',
      background: active ? color : 'white',
      color: active ? 'white' : '#333',
      border: `1px solid ${active ? color : '#ddd'}`,
      borderRadius: '6px',
      fontSize: '13px',
      cursor: 'pointer',
      textAlign: 'left'
    }}
  >
    {label}
  </button>
);

export default OwnerDashboard;
